// Signup API Service
// This file defines the API contract for frictionless signup flow
// TODO: Backend API endpoints need to be implemented at /api/v1/frictionless/website/*

import axios from 'axios';

/**
 * Request payload for website signup
 * Sent to: POST /api/v1/frictionless/website/signup
 */
export interface WebsiteSignupRequest {
  email: string;
  name: string;
  company_name?: string;
  recaptcha_token: string;
  app_name: string;
  app_variant: string;
  redirect_to: string;
}

/**
 * Account progress data returned when existing account is detected
 */
export interface AccountProgressData {
  email: string;
  current_status: string;
  progress_percentage: number;
  friendly_milestone: string;
  has_snowflake_account: boolean;
  has_runner: boolean;
  created_at: string;
  app_name?: string | null;
  app_variant?: string | null;
}

/**
 * Response from website signup endpoint
 * Expected from: POST /api/v1/frictionless/website/signup
 *
 * Response statuses:
 * - 'success': New signup created successfully
 * - 'existing_account_created_signup': Existing member without signup (edge case)
 * - 409 response returns error_type 'EXISTING_ACCOUNT' when signup already exists
 */
export interface WebsiteSignupResponse {
  status: string;
  signup_id?: number;
  message: string;
  error_type?: string;
  data?: AccountProgressData;
}

/**
 * Request payload for runner provisioning
 * Sent to: POST /api/v1/frictionless/website/provision-runner
 *
 * Note: This is intentionally empty as the endpoint uses the authenticated user
 * to identify which signup to provision a runner for. The endpoint will
 * automatically check for Snowflake accounts associated with the user's signup.
 */
export type RunnerProvisionRequest = Record<string, never>;

/**
 * Response from runner provisioning endpoint
 * Expected from: POST /api/v1/frictionless/website/provision-runner
 */
export interface RunnerProvisionResponse {
  status: string;
  runner_id: number;
  message: string;
  task_id: string;
  detail?: string;
}

/**
 * Signup status enum matching backend SignupStatus
 */
export enum SignupStatus {
  SIGNUP_CREATED = 'signup_created',
  SNOWFLAKE_ACCOUNT_CREATED = 'snowflake_account_created',
  RUNNER_PROVISIONING = 'runner_provisioning',
  RUNNER_PROVISIONING_FAILED = 'runner_provisioning_failed',
  RUNNER_PROVISIONED = 'runner_provisioned',
  SIGNUP_COMPLETED = 'signup_completed',
}

/**
 * Response from signup status check
 * Expected from: GET /api/v1/frictionless/website/status
 */
export interface SignupStatusResponse {
  source: string;
  has_snowflake_account: boolean;
  has_runner: boolean;
  current_step: SignupStatus;
  created_at: string;
  company_name?: string;
  app_name?: string;
  app_variant?: string;
}

/**
 * Response from password reset request
 * Expected from: POST /api/v1/frictionless/website/request-password-reset
 */
export interface PasswordResetResponse {
  reset_url: string;
}

/**
 * Response from the public signup availability probe.
 * Expected from: GET /api/v1/frictionless/website/signup-availability
 *
 * `enabled` is backed by the same PostHog feature flag that gates the
 * POST /signup endpoint, so the UI and the API stay in sync.
 */
export interface SignupAvailabilityResponse {
  enabled: boolean;
}

/**
 * API error response structure
 */
export interface ApiErrorResponse {
  error: string;
  details?: Record<string, string[]>;
}

/**
 * Django Ninja validation error detail structure
 */
export interface ValidationErrorDetail {
  type: string;
  loc: string[];
  msg: string;
  ctx?: {
    reason?: string;
  };
}

/**
 * Django Ninja validation error response structure
 */
export interface DjangoValidationErrorResponse {
  detail: ValidationErrorDetail[];
}

/**
 * Field validation error mapping
 * Maps field names to their error messages
 */
export interface FieldValidationErrors {
  [fieldName: string]: string;
}

/**
 * Signup API service object
 * Contains all API calls for the frictionless signup flow
 */
export const signupApi = {
  /**
   * Submit a new website signup
   * @param data - Signup form data including journey configuration
   * @returns Promise with signup response including signup_id
   *
   * TODO: Backend endpoint must be implemented
   * Endpoint: POST /api/v1/frictionless/website/signup
   *
   * Expected behavior:
   * 1. Validate recaptcha_token with Google
   * 2. Create user in Auth0 with email/name
   * 3. Send welcome email with password reset link
   * 4. Store signup record with journey info
   * 5. Return signup_id for tracking
   */
  async signup(data: WebsiteSignupRequest): Promise<WebsiteSignupResponse> {
    // TODO: This will fail until backend is implemented
    // For now, the error will be caught in the Redux thunk
    const response = await axios.post<WebsiteSignupResponse>('/api/v1/frictionless/website/signup', data);
    return response.data;
  },

  /**
   * Provision a runner for the authenticated user
   * @returns Promise with runner provisioning response
   *
   * Endpoint: POST /api/v1/frictionless/website/provision-runner
   *
   * Expected behavior:
   * 1. Verify user is authenticated
   * 2. Get user's website signup record
   * 3. Verify user has Snowflake account configured
   * 4. Create Kubernetes runner resources
   * 5. Return task_id for status tracking
   */
  async provisionRunner(): Promise<RunnerProvisionResponse> {
    const response = await axios.post<RunnerProvisionResponse>(
      '/api/v1/frictionless/website/provision-runner',
      {}, // Empty payload
    );
    return response.data;
  },

  /**
   * Get the current user's signup status
   * @returns Promise with current signup status or null if no signup exists
   *
   * Endpoint: GET /api/v1/frictionless/website/status
   *
   * Expected behavior:
   * 1. Get authenticated user from request
   * 2. Look up signup record for user
   * 3. Check for Snowflake account
   * 4. Check runner provisioning status
   * 5. Return current step in journey
   */
  async getCurrentUserSignupStatus(): Promise<SignupStatusResponse | null> {
    try {
      const response = await axios.get<SignupStatusResponse>('/api/v1/frictionless/website/status');
      return response.data;
    } catch (error: any) {
      // If 404, no signup exists for this user
      if (error?.response?.status === 404) {
        return null;
      }
      throw error;
    }
  },

  /**
   * Request a password reset link for an existing account
   * @param email - Email address of the account to reset password for
   * @returns Promise with password reset URL
   *
   * Endpoint: POST /api/v1/frictionless/website/request-password-reset
   *
   * Expected behavior:
   * 1. Generate Auth0 password reset URL
   * 2. Return reset URL that redirects to /onboarding after completion
   * 3. Return generic error for non-existent accounts (email enumeration protection)
   */
  async requestPasswordReset(email: string): Promise<PasswordResetResponse> {
    const response = await axios.post<PasswordResetResponse>('/api/v1/frictionless/website/request-password-reset', {
      email,
    });
    return response.data;
  },

  /**
   * Check whether website signups are currently being accepted.
   *
   * Called by `SignupView` on mount so the form can render a "signups
   * currently disabled" screen instead of letting the user fill in personal
   * details only to be rejected by `POST /signup` with a 400.
   *
   * Endpoint: GET /api/v1/frictionless/website/signup-availability
   *
   * If this probe itself errors we treat that as "enabled" - the worst case
   * is that the user gets the existing 400 error banner from the submit
   * path, which is no worse than before this check existed.  This is
   * implemented by the caller (the Redux thunk) so the API contract here
   * stays minimal.
   */
  async getSignupAvailability(): Promise<SignupAvailabilityResponse> {
    const response = await axios.get<SignupAvailabilityResponse>('/api/v1/frictionless/website/signup-availability');
    return response.data;
  },
};

/**
 * Type guard to check if error is an API error response
 */
export function isApiError(error: unknown): error is { response: { data: ApiErrorResponse } } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: unknown }).response === 'object' &&
    (error as { response?: { data?: unknown } }).response !== null &&
    'data' in ((error as { response: unknown }).response as object)
  );
}

/**
 * Type guard to check if error is a Django Ninja validation error
 */
export function isDjangoValidationError(
  error: unknown,
): error is { response: { data: DjangoValidationErrorResponse } } {
  if (
    typeof error === 'object' &&
    error !== null &&
    'response' in error &&
    typeof (error as { response?: unknown }).response === 'object' &&
    (error as { response?: { data?: unknown } }).response !== null
  ) {
    const data = (error as { response: { data?: unknown } }).response.data;
    return (
      typeof data === 'object' &&
      data !== null &&
      'detail' in data &&
      Array.isArray((data as { detail?: unknown }).detail)
    );
  }
  return false;
}

/**
 * Parse Django Ninja validation errors into field-specific errors
 *
 * Example input:
 * {
 *   "detail": [
 *     {
 *       "type": "value_error",
 *       "loc": ["body", "payload", "email"],
 *       "msg": "value is not a valid email address: The part after the @-sign is not valid. It should have a period.",
 *       "ctx": { "reason": "The part after the @-sign is not valid. It should have a period." }
 *     }
 *   ]
 * }
 *
 * Output: { "email": "The part after the @-sign is not valid. It should have a period." }
 */
export function parseFieldValidationErrors(error: unknown): FieldValidationErrors | null {
  if (!isDjangoValidationError(error)) {
    return null;
  }

  const fieldErrors: FieldValidationErrors = {};

  error.response.data.detail.forEach((detail) => {
    // Extract field name from loc array (last element is usually the field name)
    // loc is typically ["body", "payload", "email"] so we take the last element
    const fieldName = detail.loc[detail.loc.length - 1];

    // Use the reason from ctx if available, otherwise use the full msg
    const errorMessage = detail.ctx?.reason ?? detail.msg;

    fieldErrors[fieldName] = errorMessage;
  });

  return fieldErrors;
}

/**
 * Extract user-friendly error message from API error
 */
export function getErrorMessage(error: unknown): string {
  if (isApiError(error)) {
    const apiError = error.response.data;
    if (apiError.details != null) {
      // Flatten validation errors into a readable message
      const messages = Object.entries(apiError.details)
        .map(([field, errors]) => `${field}: ${errors.join(', ')}`)
        .join('; ');
      return messages;
    }
    return apiError.error;
  }

  if (error instanceof Error) {
    return error.message;
  }

  return 'An unexpected error occurred';
}
